import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { FieldCard } from './FieldCard';
import { EMPTY_DECISION, FieldDecisionState } from '../decisionState';
import { FieldGroup, groupLabel, isEmptyGroup, leafKey } from '../fieldPath';

type FieldGroupPanelProps = {
  group: FieldGroup;
  ordinal: number; // 1-based position, for the header
  decisions: Record<string, FieldDecisionState>;
  onDecisionChange: (
    fieldName: string,
    patch: Partial<FieldDecisionState>
  ) => void;
  activeFieldName: string | null;
  onSelectField: (fieldName: string) => void;
  selectingFieldName: string | null;
  onToggleSelectReference: (fieldName: string) => void;
  onMarkGroupCorrect: (group: FieldGroup) => void;
  expanded: boolean;
  onToggleExpanded: (key: string, expanded: boolean) => void;
};

/** One collapsible panel per repeating-group instance. */
export const FieldGroupPanel = ({
  group,
  ordinal,
  decisions,
  onDecisionChange,
  activeFieldName,
  onSelectField,
  selectingFieldName,
  onToggleSelectReference,
  onMarkGroupCorrect,
  expanded,
  onToggleExpanded,
}: FieldGroupPanelProps) => {
  const decidedCount = group.fields.filter(
    f => decisions[f.fieldName]?.isCorrect != null
  ).length;
  const allDecided = decidedCount === group.fields.length;
  const empty = isEmptyGroup(group);

  // Shown in the header so a collapsed panel says which site it is: site_name,
  // or else the group's first non-null value.
  const nameField = group.fields.find(f => leafKey(f.fieldName) === 'site_name');
  const headerValue =
    nameField?.value ?? group.fields.find(f => f.value != null)?.value ?? null;

  const label = `${groupLabel(group.groupPrefix)} ${ordinal}`;

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, isExpanded) => onToggleExpanded(group.key, isExpanded)}
      disableGutters
      sx={{ mb: 1 }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            width: '100%',
            pr: 1,
            minWidth: 0,
          }}
        >
          <Typography variant="body2" fontWeight={600} sx={{ flexShrink: 0 }}>
            {label}
          </Typography>

          {empty ? (
            <Typography
              variant="body2"
              color="text.secondary"
              fontStyle="italic"
              noWrap
            >
              No site data extracted
            </Typography>
          ) : (
            headerValue && (
              <Typography variant="body2" color="text.secondary" noWrap>
                {headerValue}
              </Typography>
            )
          )}

          <Box sx={{ flexGrow: 1 }} />

          <Chip
            size="small"
            variant={allDecided ? 'filled' : 'outlined'}
            color={allDecided ? 'success' : 'default'}
            label={`${decidedCount}/${group.fields.length}`}
            sx={{ flexShrink: 0 }}
          />
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ pt: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <Button
            size="small"
            variant="outlined"
            disabled={allDecided}
            onClick={() => onMarkGroupCorrect(group)}
          >
            {/* One click to confirm the model correctly found no sites. */}
            {empty ? 'No sites in this report' : 'Mark all correct'}
          </Button>
        </Box>

        {group.fields.map(field => (
          <FieldCard
            key={field.fieldName}
            field={field}
            labelOverride={leafKey(field.fieldName)}
            decision={decisions[field.fieldName] ?? EMPTY_DECISION}
            active={activeFieldName === field.fieldName}
            onSelect={() => onSelectField(field.fieldName)}
            onDecisionChange={patch => onDecisionChange(field.fieldName, patch)}
            selectingReference={selectingFieldName === field.fieldName}
            onToggleSelectReference={() => onToggleSelectReference(field.fieldName)}
          />
        ))}
      </AccordionDetails>
    </Accordion>
  );
};
